/* ═══════════════════════════════════════════════════════════════════
   MATRIKAR — Gift Reconciler (Brique A) — assets/mk-gift.js
   2026-06-08

   Auto-adds / auto-removes the Summer-Promo included gift (Premium Trunk
   Organizer — Small) based on cart eligibility, locks its qty to 1,
   and lets the customer DECLINE it for the session.

   PRICE AUTHORITY = the native Shopify BXGY automatic discount
   "Free Trunk Organizer — Summer Promo" (Buy: Diamond car mat in Full
   Coverage / 3-Row → Get: 1× Trunk Organizer Small at 100% off, scoped
   to the 3 Small variants, qty-capped 1). This script only ADDS /
   REMOVES the line; it NEVER sets a price. The `_free_gift` property is
   a display/identification marker ONLY — never the price authority.

   Independent module. Does NOT touch image zoom (mk-image-zoom), qty UI,
   cross-sell, or checkout. Loaded globally (defer) via layout/theme.liquid.
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ---- config (MUST mirror the BXGY promo scope) ---- */
  var GIFT_DEFAULT_VARIANT = 45931833098401;                              // All Black · Small (default colour)
  var GIFT_VARIANTS = [45931833098401, 45931832672417, 45931831951521];  // All Black / Black&Red / Black&White · Small
  var MARKER = 'diamond-trunk-small';                                     // _free_gift property value
  var DECLINE_KEY = 'mk_gift_declined';                                   // sessionStorage flag (decision #2)

  /* ---- loop-protection + decline state ---- */
  var reconciling = false;   // GUARD 1 — mutex: a reconcile pass is in-flight
  var pendingRun = false;    //          coalesce: a change arrived while reconciling
  var debounceTimer = null;  // GUARD 3 — debounce bursts of events
  var prevHadGift = false;   // was a gift line present at the end of the LAST pass?
                             // (used to tell a USER removal apart from "never added yet")

  /* ---- decline helpers (sessionStorage = lives for the browser session only) ---- */
  function isDeclined() {
    try { return sessionStorage.getItem(DECLINE_KEY) === '1'; } catch (e) { return false; }
  }
  function setDeclined() {
    try { sessionStorage.setItem(DECLINE_KEY, '1'); } catch (e) {}
  }

  /* ---- cart helpers ---- */
  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }
  // Coverage = last "/"-segment of variant_title, normalised so the Black & White
  // quirk "… / 3 - Row Set" (extra spaces) still matches.
  function normCoverage(variantTitle) {
    if (!variantTitle) return '';
    var parts = variantTitle.split('/');
    var seg = parts.length > 1 ? parts[parts.length - 1] : '';
    return seg.toLowerCase().replace(/[\s\-]/g, ''); // strip spaces + dashes
  }
  function isEligibleDiamond(item) {
    var pt = (item.product_type || '').toLowerCase();
    var title = (item.product_title || '').toLowerCase();
    if (pt !== 'car mats' || title.indexOf('diamond') === -1) return false; // Diamond CAR MAT only — product_type renommé 'Custom Car Mats' → 'Car Mats' (2026-06)
    var cov = normCoverage(item.variant_title);
    return cov.indexOf('fullcoverage') !== -1 || cov.indexOf('3row') !== -1;
  }
  function isGiftLine(item) {
    var p = item.properties || {};
    return p._free_gift === MARKER || (GIFT_VARIANTS.indexOf(item.id) !== -1 && !!p._free_gift);
  }
  function addGift() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: GIFT_DEFAULT_VARIANT, quantity: 1, properties: { _free_gift: MARKER } })
    });
  }
  function updateLines(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
  }
  function zeroMap(lines) { var m = {}; lines.forEach(function (l) { m[l.key] = 0; }); return m; }

  /* ---- the reconciler ---- */
  function reconcile() {
    if (reconciling) { pendingRun = true; return; }   // GUARD 1 — never two passes at once
    reconciling = true;
    var hadGiftBefore = prevHadGift;                   // snapshot of the previous pass's end state
    return getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var eligible = items.some(isEligibleDiamond);
      var gifts = items.filter(isGiftLine);

      // GUARD 2 — idempotency: each branch makes its own trigger condition false afterwards.

      // (1) Diamond gone -> auto-remove the gift. SYSTEM removal, NOT a decline.
      if (!eligible && gifts.length > 0) {
        prevHadGift = false;
        return updateLines(zeroMap(gifts));
      }
      // (2) safety: collapse duplicate gifts to a single line.
      if (gifts.length > 1) {
        prevHadGift = true;
        return updateLines(zeroMap(gifts.slice(1)));
      }
      // (3) eligible + gift present -> just enforce qty 1.
      if (eligible && gifts.length === 1) {
        prevHadGift = true;
        if (gifts[0].quantity !== 1) { var q = {}; q[gifts[0].key] = 1; return updateLines(q); }
        return null;
      }
      // (4) eligible + NO gift -> add it, UNLESS declined.
      if (eligible && gifts.length === 0) {
        if (isDeclined()) { prevHadGift = false; return null; }       // (4a) session decline → never re-add
        if (hadGiftBefore) {                                          // (4b) gift was here last pass & we
          setDeclined();                                             //      didn't remove it (still eligible)
          prevHadGift = false;                                       //      → the USER trashed it → decline
          return null;                                              //      and DO NOT re-add this session
        }
        return addGift().then(function () { prevHadGift = true; });   // (4c) first add of the session
      }
      // (5) not eligible + no gift -> nothing.
      prevHadGift = false;
      return null;
    }).catch(function () {
      /* swallow — never block the cart */
    }).then(function () {
      reconciling = false;
      if (pendingRun) { pendingRun = false; schedule(); }            // run once more for a coalesced change
    });
  }

  function schedule() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 20);                       // GUARD 3 — debounce court (120→20ms) : le cadeau part quasi immédiatement après l'add ; le mutex `reconciling` empêche les double-passes
  }

  /* ---- triggers ---- */

  // (a) theme cart events (user ATC via the product form)
  ['cart:change', 'cart:updated', 'variant:add'].forEach(function (ev) {
    document.addEventListener(ev, schedule);
  });

  // (b) EVERY cart mutation — also covers the drawer's own trash / qty buttons,
  //     which call /cart/update.js WITHOUT dispatching a cart event.
  //     GUARD 4 — ignore fetches WE initiated (reconciling === true): the gift
  //     add/remove/qty calls fire inside reconcile() while the mutex is held, so
  //     they never schedule another pass → no echo loop.
  var _origFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    return _origFetch.apply(this, args).then(function (res) {
      try {
        var url = (args[0] || '').toString();
        var method = ((args[1] && args[1].method) || 'GET').toUpperCase();
        if (method === 'POST' && /\/cart\/(add|update|change|clear)/.test(url) && !reconciling) {
          schedule();
        }
      } catch (e) { /* noop */ }
      return res;
    });
  };

  /* ═══════════════════════════════════════════════════════════════════
     GIFT LINE LABEL — "INCLUDED" badge on the gift row (display only).
     ONE shared module decorates the gift row of BOTH surfaces:
       • drawer    : .cart-item[data-key]            (rebuilt by cart-drawer.js)
       • cart page : .cart-page-item[data-line-key]  (server-rendered)
     The gift row is matched by line key === the gift line's key in /cart.js.
     NO colour selector — the gift stays All Black (added by the reconciler).
     Idempotent ; a MutationObserver re-applies it after every re-render.
     ═══════════════════════════════════════════════════════════════════ */

  var STYLE_ID = 'mk-gift-ui-style';
  function injectStyleOnce() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      '.mk-gift-badge{display:inline-flex;align-items:center;gap:4px;background:var(--mk-red,#D71A23);color:#fff;font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;padding:3px 8px;border-radius:var(--mk-radius-md,6px);white-space:nowrap;margin:2px 0 8px;font-family:var(--mk-font,"Urbanist",sans-serif)}' +
      /* Sous-texte du badge INCLUDED (cadeau offert uniquement) — discret, rouge Matrikar */
      '.mk-gift-reserved{display:block;font-size:11px;font-weight:600;color:var(--mk-red,#D71A23);line-height:1.3;margin:3px 0 8px;font-family:var(--mk-font,"Urbanist",sans-serif)}' +
      /* Skeleton "le cadeau arrive" — injecté dès l'ouverture du drawer, remplacé par la vraie ligne cadeau */
      '.mk-gift-skeleton{display:flex;align-items:center;gap:12px;padding:14px 0;opacity:.9;border-bottom:1px solid rgba(3,2,19,.06);font-family:var(--mk-font,"Urbanist",sans-serif)}' +
      '.mk-gift-skeleton__thumb{width:56px;height:56px;border-radius:var(--mk-radius-md,6px);object-fit:cover;flex:none;background:var(--mk-light,#F4F4F6)}' +
      '.mk-gift-skeleton__txt{min-width:0}' +
      '.mk-gift-skeleton__line{display:flex;align-items:center;color:var(--mk-grey,#5E5E70);font-size:13px;font-weight:600;margin-top:6px}' +
      '.mk-gift-spinner{display:inline-block;width:12px;height:12px;border:2px solid rgba(215,26,35,.25);border-top-color:var(--mk-red,#D71A23);border-radius:50%;margin-right:7px;animation:mkGiftSpin .7s linear infinite}' +
      '@keyframes mkGiftSpin{to{transform:rotate(360deg)}}';
    document.head.appendChild(s);
  }

  function buildGiftBadge() {
    injectStyleOnce();
    var badge = document.createElement('span');
    badge.className = 'mk-gift-badge';
    badge.setAttribute('data-mk-gift-ui', '1');
    badge.textContent = '🎁 INCLUDED';
    return badge;
  }

  // Sous-texte affiché SOUS le badge INCLUDED, uniquement sur la vraie ligne cadeau (organizer offert).
  function buildReservedLine() {
    var line = document.createElement('div');
    line.className = 'mk-gift-reserved';
    line.setAttribute('data-mk-gift-ui', '1');
    line.textContent = 'Your free organizer is reserved';
    return line;
  }

  /* ---- skeleton "le cadeau arrive" (DRAWER uniquement) ---- */
  var GIFT_THUMB = 'https://cdn.shopify.com/s/files/1/0648/3515/8177/files/Promo-free-2.jpg';
  var skeletonTimer = null;       // force-remove si l'add n'arrive jamais
  var skeletonGaveUp = false;     // après timeout, on cesse de ré-injecter
  var SKELETON_TIMEOUT = 5000;

  function buildSkeleton() {
    injectStyleOnce();
    var row = document.createElement('div');
    row.className = 'mk-gift-skeleton';
    row.setAttribute('data-mk-gift-skeleton', '1');
    row.setAttribute('aria-live', 'polite');
    var img = document.createElement('img');
    img.className = 'mk-gift-skeleton__thumb';
    img.src = GIFT_THUMB; img.alt = ''; img.width = 56; img.height = 56; img.loading = 'lazy';
    var txt = document.createElement('div');
    txt.className = 'mk-gift-skeleton__txt';
    var line = document.createElement('div');
    line.className = 'mk-gift-skeleton__line';
    line.innerHTML = '<span class="mk-gift-spinner" aria-hidden="true"></span>Adding your trunk organizer…';
    txt.appendChild(buildGiftBadge());
    txt.appendChild(line);
    row.appendChild(img);
    row.appendChild(txt);
    return row;
  }

  // Affiche/retire le squelette dans le DRAWER. Idempotent (un seul squelette) +
  // timeout de sécurité 5s pour qu'un add échoué ne laisse pas un spinner figé.
  function syncSkeleton(show) {
    var container = document.getElementById('mkCartItems');
    if (!container) return;
    var existing = container.querySelector('.mk-gift-skeleton');
    if (show && !skeletonGaveUp) {
      if (!existing) container.appendChild(buildSkeleton());
      if (!skeletonTimer) {
        skeletonTimer = setTimeout(function () {
          skeletonTimer = null; skeletonGaveUp = true;
          var c = document.getElementById('mkCartItems');
          var sk = c && c.querySelector('.mk-gift-skeleton');
          if (sk && sk.parentNode) sk.parentNode.removeChild(sk);
        }, SKELETON_TIMEOUT);
      }
    } else {
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      if (skeletonTimer) { clearTimeout(skeletonTimer); skeletonTimer = null; }
      skeletonGaveUp = false;
    }
  }

  function applyDecorate(cart) {
    var items = (cart && cart.items) || [];
    var gifts = items.filter(isGiftLine);
    var gift = gifts.length ? gifts[0] : null;
    var rows = document.querySelectorAll('.cart-item[data-key], .cart-page-item[data-line-key]');
    Array.prototype.forEach.call(rows, function (row) {
      var key = row.getAttribute('data-key') || row.getAttribute('data-line-key');
      var existing = row.querySelector('.mk-gift-badge');
      if (gift && key === gift.key) {
        if (existing) return;                               // already labelled
        var badge = buildGiftBadge();
        var info = row.querySelector('.cart-item-info') || row.querySelector('.cart-page-info') || row;
        var name = info.querySelector('.cart-item-name') || info.querySelector('.cart-page-name');
        if (name && name.nextSibling) info.insertBefore(badge, name.nextSibling);
        else if (name) info.appendChild(badge);
        else info.insertBefore(badge, info.firstChild);
        // Ligne "Your free organizer is reserved" juste sous le badge INCLUDED (cadeau offert only)
        var reserved = buildReservedLine();
        if (badge.nextSibling) info.insertBefore(reserved, badge.nextSibling);
        else info.appendChild(reserved);
      } else if (existing) {
        existing.parentNode.removeChild(existing);          // stale (row no longer the gift)
        var staleReserved = row.querySelector('.mk-gift-reserved');
        if (staleReserved && staleReserved.parentNode) staleReserved.parentNode.removeChild(staleReserved);
      }
    });

    // SKELETON : éligible Diamond (FC/3-Row) + cadeau pas encore là + pas décliné → "le cadeau arrive".
    // Jamais pour Front Only (non éligible) ni si décliné. window.currentCart est frais au render #1 du drawer.
    var eligiblePending = items.some(isEligibleDiamond) && !gift && !isDeclined();
    syncSkeleton(eligiblePending);
  }

  var decorateTimer = null;
  function scheduleDecorate() {
    clearTimeout(decorateTimer);
    decorateTimer = setTimeout(function () {
      if (window.currentCart) applyDecorate(window.currentCart);
      else getCart().then(applyDecorate).catch(function () {});
    }, 60);
  }

  function observeSurface(el) {
    if (!el || typeof MutationObserver === 'undefined') return;
    new MutationObserver(scheduleDecorate).observe(el, { childList: true, subtree: true });
  }
  function initGiftUI() {
    observeSurface(document.getElementById('mkCartItems'));       // drawer line-items container
    observeSurface(document.querySelector('.cart-page-items'));   // full cart page line-items container
    ['cart:change', 'cart:updated', 'variant:add'].forEach(function (ev) {
      document.addEventListener(ev, scheduleDecorate);
    });
    scheduleDecorate();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGiftUI);
  } else {
    initGiftUI();
  }

  // (c) initial load — an eligible Diamond may already be in the cart.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
